// Considered: FilterGroupEditor's GroupNode — rejected as an extension because
// its state is `SimpleFilterGroup` (`filters: FilterExpression[]`), a shape that
// re-wraps a lane condition on every read/write. This molecule holds the
// canonical `FilterGroup` (`expressions: FilterGroupLeaf[]`, `groups?`) so a
// perspective/relationship/arc condition round-trips with no re-wrap — see
// spec Reuse Audit ("UnifiedFilterTreeMolecule (+ FilterGroupNodeMolecule)").
// `FilterGroupEditor` is the thing this replaces (uf-simple/uf-advanced).
// Operator controls: `SegmentedControlAtom` for the full all/any header;
// the narrow compact chip is `FilterJoinerAtom`'s `quantifier` variant — the
// same ButtonAtom-based control as the index-1 joiner, never a second
// hand-rolled toggle (SegmentedControlAtom's two side-by-side segments don't
// fit the one-word chip a narrow nested header has room for).
import React, { useCallback } from 'react';
import { useTranslation } from 'localization';
import { isPropertyLeaf } from '@center-platform/spheres/filters';
import type { FilterExpression, FilterGroup, FilterGroupLeaf } from 'types/filters';
import type { OrgAvailableTypeSchema } from 'types/schema';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import { SegmentedControlAtom } from '../../atoms/SegmentedControl';
import FilterJoinerAtom from '../../atoms/FilterJoiner/FilterJoinerAtom';
import EditableFilterRowMolecule from '../EditableFilterRow/EditableFilterRowMolecule';
import LaneFilterRowMolecule from '../LaneFilterRow/LaneFilterRowMolecule';
import { PropertyOption, PropertyType } from '../PropertyFilter/PropertyFilterMolecule';
import type { ArcTemplateOption } from '../../../hooks/useArcTemplates';
import type { RelationshipOption } from '../RelationshipFilter/RelationshipFilterMolecule';
import type { PerspectiveOption } from '../PerspectiveFilter/PerspectiveFilterMolecule';
import { nextFilterId } from '../../../utils/ids';
import { setFilterDragPayload } from '../../../utils/filterDnd';
import './FilterGroupNodeMolecule.scss';

/** A leaf's own stable drag id — `undefined` when it has none yet (never dragged). */
function leafDragId(leaf: FilterGroupLeaf): string | undefined {
  return isPropertyLeaf(leaf) ? leaf.id : leaf.filter.id;
}

// Only a property leaf carries `id` at the top level; every lane leaf nests
// its stable id one level down, on `.filter.id` (same client-minted-id
// contract, different discriminant). `leaf.id` alone type-errors on
// `PerspectiveFilterLeaf`/`RelationshipFilterLeaf`/`ArcFilterLeaf` because
// none of them declare it — this is the narrowed read, not a shortcut.
function leafKey(leaf: FilterGroupLeaf, index: number): string | number {
  return (isPropertyLeaf(leaf) ? leaf.id : leaf.filter.id) ?? index;
}

export interface FilterGroupNodeMoleculeProps {
  /** The canonical group this node renders — never re-wrapped on read or write. */
  group: FilterGroup;
  onChange: (group: FilterGroup) => void;
  /** Omit at the tree root — a root group is never removable. */
  onRemove?: () => void;
  depth?: number;
  isRoot?: boolean;
  /**
   * Suppress this node's own header — used when a framed host (e.g.
   * `UnifiedFilterTreeMolecule`) already renders the match/operator bar above it.
   */
  hideHeader?: boolean;
  /** False on a host whose evaluator cannot resolve a nested group (rare). */
  allowGroups?: boolean;
  availableProperties: PropertyOption[];
  excludePropertyTypes?: PropertyType[];
  /**
   * Sphere-type names each property leaf's own in-row property switcher is
   * scoped to — forwarded to `EditableFilterRowMolecule`'s catalog and to
   * every nested group's leaves unchanged (the scope is derived once, at the
   * tree root, from the merged filter tiers — never recomputed per depth).
   */
  scopeTypeLabels?: string[];
  arcTemplates?: ArcTemplateOption[];
  availableRelationships?: RelationshipOption[];
  familyTypeSchemas?: OrgAvailableTypeSchema[];
  availablePerspectives?: PerspectiveOption[];
  perspectiveSearchProps?: {
    schemaSystemName: string;
    sphereId: string;
    direction?: 'outwards' | 'inwards' | 'both';
    hops?: number;
  };
  /**
   * Forwarded to every `perspective` lane leaf's `LaneFilterRowMolecule` —
   * locks that leaf's perspective picker and starts it in Specific mode. A
   * `focusPerspective` column host sets this on the tree root
   * (`UnifiedFilterTreeMolecule`'s `lockPerspective`); every other host omits
   * it.
   */
  lockPerspective?: boolean;
  /**
   * Forwarded to every `arc` lane leaf's `LaneFilterRowMolecule` — locks that
   * leaf's fused arc-selector chrome. A `focusArc` column host sets this on
   * the tree root (`UnifiedFilterTreeMolecule`'s `lockArc`); every other host
   * omits it.
   */
  lockArc?: boolean;
  /**
   * Forwarded to every property leaf's `EditableFilterRowMolecule` — hides its
   * editor-footer Apply button (OQ-7). Set by a column host's
   * `UnifiedFilterTreeMolecule` (`isColumnPopover`); every other host omits it.
   */
  hideApplyButton?: boolean;
  /**
   * Renders the "Add condition" picker (the aspect catalog) anchored below the
   * button. `onPick` appends the leaf and closes the picker; `onClose` cancels
   * without adding. Omitted while the catalog isn't wired to a host's aspect
   * data yet — the button still renders (skeleton).
   */
  renderCatalog?: (opts: { onPick: (leaf: FilterGroupLeaf) => void; onClose: () => void }) => React.ReactNode;
  className?: string;
  /**
   * Parity instrumentation — prefix for this node's own datum ids (`.toggle`,
   * `.remove-group`, `.label`/`.label#2`, `.condition`, `.add-group`).
   * Defaults to the uf-simple embed's block; the advanced dialog mounts the
   * same node under `uf-advanced` (see `UnifiedFilterTreeMolecule`'s
   * `datumScope`, forwarded down through every recursive nesting).
   */
  datumScope?: string;
  /**
   * Overrides the root-only segmented control's id; defaults to
   * `${datumScope}.tabs`. The advanced dialog already spends `uf-advanced.tabs`
   * on its own mode toggle, so its root group tags this as `.tabs#2` instead.
   */
  rootTabsDatumId?: string;
  /**
   * Overrides a NON-root node's own header ids (`.toggle`/`.remove-group`).
   * The framed uf-column session tree needs a distinct pair from its own
   * bare-mount siblings (`.toggle#3`/`.remove-group#2`) since the same
   * component renders both without varying by `framed`.
   */
  nestedToggleDatumId?: string;
  nestedRemoveDatumId?: string;
  /**
   * Overrides a child joiner's own datum id — one for the read-only echo
   * (`.label`/index 0 or 2+), one for the editable control (`.or`/index 1).
   * Applied to every child position (leaf or nested group alike; the
   * prototype's `each(node.children)` doesn't distinguish). Defaults to the
   * existing per-branch id (`.label#2` for a leaf child, `.label` for a
   * nested-group child) when omitted, so a host that doesn't supply these
   * keeps today's behaviour unchanged.
   */
  nestedJoinerLabelDatumId?: string;
  nestedJoinerControlDatumId?: string;
  /**
   * Parity instrumentation — overrides the root's `data-container` (defaults
   * to `'ui-tree'`, the uf-simple embed's implementation section). The
   * column adapter's session tier mounts this same node under `uf-column`'s
   * own section, `'ui-column'`.
   */
  container?: string;
  /**
   * The group id the rail's own additions currently land in (advanced-filters-v2.jsx
   * `focusId`) — `undefined`/root means "no focus, add to the root". Forwarded
   * recursively so any depth can compare itself against it.
   */
  focusedGroupId?: string;
  /**
   * Fired when this (non-root) group is clicked, or when it creates a new
   * child group — either way, this group becomes where the rail's own
   * additions land next. Omitted hosts (e.g. the framed uf-simple embed,
   * which has no rail) simply never focus.
   */
  onFocusGroup?: (groupId: string) => void;
  /** No edit/remove/add affordances anywhere in the tree; rows render their own read-only chrome. */
  readOnly?: boolean;
  /**
   * Narrow-host layout: nested groups render the indent-rule "depth rule"
   * instead of a card-in-card border, the header collapses to a compact
   * all/any chip (no eyebrow, no item count), the joiner sits above each
   * row instead of in a gutter beside it, and nesting is capped at depth 1
   * ("Deeper nesting — use Advanced" replaces "Add group").
   * `UnifiedFilterTreeMolecule`'s `layout==='narrow'`.
   */
  narrow?: boolean;
  /**
   * Whether a narrow layout caps group nesting at depth 1 (default `true`).
   * A column/popover host caps it and points the author at the Advanced
   * editor for anything deeper ("Deeper nesting — use Advanced" replaces "Add
   * group", and drag-to-group refuses a deeper target). The Advanced editor IS
   * that destination, so it passes `false`: every depth keeps "Add group", as
   * the prototype's Advanced tree does — never a hint sending the author to
   * the surface they are already in. Ignored on a wide layout, which never caps.
   */
  capNarrowNesting?: boolean;
  /**
   * Opt-in drag-to-group (advanced-filters-v2.jsx `AFDnD`): a host without
   * `dnd` renders no grips and no drop targets at all, and neither does a
   * `readOnly` tree (moving a node is an edit). The actual drag
   * state (which node is being dragged) is owned by the top-level tree —
   * this node only renders the affordances and reports intent through
   * `onDragStartNode` / `onDragEndNode` / `canDropNode` / `onDropNode`.
   */
  dnd?: boolean;
  /** The id of the node currently being dragged, or `null`/`undefined` if none. */
  dragNodeId?: string | null;
  /** Fired when this node's own grip starts a drag on one of its children. */
  onDragStartNode?: (nodeId: string) => void;
  /** Fired when a drag ends (dropped or cancelled), win or lose. */
  onDragEndNode?: () => void;
  /** Whether `nodeId` may be dropped onto the group identified by `targetGroupId`. */
  canDropNode?: (nodeId: string, targetGroupId: string) => boolean;
  /** Commits a drop: relocate `nodeId` into the group `targetGroupId`. */
  onDropNode?: (nodeId: string, targetGroupId: string) => void;
  /**
   * The group id the keyboard drag-to-group cursor currently previews as the drop target
   * (`UnifiedFilterTreeMolecule`'s `useLiftAndMove` in `'preview'` mode) — `null`/`undefined`
   * when no keyboard lift is in progress. Drawn with the same highlight as a mouse drag-over.
   */
  keyboardCursorGroupId?: string | null;
  /**
   * Wired to a grip's own `onKeyDown`. Space/Enter begins a lift (reporting the grip's node
   * id and this group's own id — the lift's starting position); once lifted, Arrow keys move
   * the cursor, Enter drops, Escape cancels — all driven by `UnifiedFilterTreeMolecule`'s hook,
   * which ignores the arguments past the initial lift.
   */
  onGripKeyDown?: (e: React.KeyboardEvent, nodeId: string, currentGroupId: string) => void;
  /** Wired to a grip's own `onBlur` — losing focus mid-lift cancels it. */
  onGripBlur?: () => void;
  /**
   * Narrow hosts replace the floating `renderCatalog` anchor with the
   * tree's own drill-in container (`UnifiedFilterTreeMolecule`): supplying
   * this makes "Add condition" report the target group id instead of
   * opening `renderCatalog` locally.
   */
  onRequestAddCondition?: (groupId: string) => void;
  /**
   * Host-supplied per-leaf problem (a contradiction with a locked tier),
   * keyed by the leaf's own id — never persisted, computed fresh per
   * render. Each property leaf's message is passed to its
   * `EditableFilterRowMolecule` as `problem`, and the callback is forwarded
   * unchanged to every nested group.
   */
  problemOf?: (leafId: string) => { title: string; message: string } | undefined;
  /**
   * The id of the leaf most recently added via "Add condition" (either this
   * node's own floating catalog, or a narrow host's drill-in reporting back
   * through `UnifiedFilterTreeMolecule`) — the matching row opens straight to
   * its value instead of the closed summary (EAINT-931). Forwarded to every
   * nested group unchanged; `undefined` once nothing was just added.
   */
  justAddedLeafId?: string;
  /**
   * Reports the id of a leaf this node just added through its own floating
   * catalog (`addCondition`) — `UnifiedFilterTreeMolecule` is the single
   * owner of `justAddedLeafId` state, so a node-local add must hand the new
   * id back up rather than tracking it itself.
   */
  onLeafAdded?: (leafId: string) => void;
  /**
   * Host override: every property row opens straight to its value, not the
   * closed summary — the context editor's flat lists (`ContextLevelInspectorOrganism`,
   * `SegmentPanelMolecule`, `ApplicabilityEditorMolecule`) are edited immediately
   * on mount, unlike the advanced dialog's accordion-of-closed-rows default.
   * Still OR'd with the existing just-added tracking below, so a row added
   * after mount opens too even on a host that leaves this `false`. Opens
   * only — focus moves solely into the just-added row (`autoFocusValue`).
   */
  defaultOpen?: boolean;
  /**
   * `true` when this group's operator is not the author's to change — the host stores the
   * list with a fixed conjunction (`UnifiedFilterTreeMolecule`'s `fixedOperator`). The
   * joiners then render as the read-only echo, never as a control.
   */
  operatorFixed?: boolean;
  /**
   * Forwarded unchanged to every property leaf's `EditableFilterRowMolecule`
   * (and recursively to every nested group) — see that prop's own doc
   * comment. `UnifiedFilterTreeMolecule`'s single passthrough for the rail's
   * "focused row" tracking (EAINT-936 Phase 1).
   */
  onValueFocus?: (filter: FilterExpression) => void;
}

// Return type guarantees `id: string` (not `FilterGroup`'s optional `id?`) so
// callers minting a fresh group — `addGroup` below — never lose the
// just-assigned id back to `string | undefined`.
const newGroup = (): FilterGroup & { id: string } => ({
  id: nextFilterId(),
  operator: 'and',
  expressions: [],
});

interface GroupNodeClassState {
  isRoot: boolean;
  focused: boolean;
  compact: boolean;
  narrow: boolean;
  dragOver: boolean;
  canDropHere: boolean;
  isKeyboardCursor: boolean;
  className: string;
}

/** The node root's BEM class list for its current role, layout and drag state. */
function groupNodeClassName(state: GroupNodeClassState): string {
  const dropHighlighted = (state.dragOver && state.canDropHere) || state.isKeyboardCursor;
  return [
    'filter-group-node-molecule',
    state.isRoot ? 'filter-group-node-molecule--root' : 'filter-group-node-molecule--nested',
    state.focused ? 'filter-group-node-molecule--focused' : '',
    state.compact ? 'filter-group-node-molecule--compact' : '',
    // Wide hosts lay every row out against one shared joiner gutter column.
    state.narrow ? '' : 'filter-group-node-molecule--gutter',
    dropHighlighted ? 'filter-group-node-molecule--drag-over' : '',
    state.className,
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * One node of the recursive unified-filter tree: an all/any toggle, its
 * `expressions` (leaves — property rows and lane conditions alike) and nested
 * `groups`, joined by a `FilterJoinerAtom` gutter, plus its own add
 * affordances. Identical shape at every depth (advanced-filters-v2.jsx
 * `AFGroupNode`) — the design's `isRoot`/`hideHeader` cases are this
 * component's own props, not a separate component.
 */
const FilterGroupNodeMolecule: React.FC<FilterGroupNodeMoleculeProps> = ({
  group,
  onChange,
  onRemove,
  depth = 0,
  isRoot = false,
  hideHeader = false,
  allowGroups = true,
  availableProperties,
  excludePropertyTypes,
  scopeTypeLabels,
  arcTemplates,
  availableRelationships,
  familyTypeSchemas,
  availablePerspectives,
  perspectiveSearchProps,
  lockPerspective = false,
  lockArc = false,
  hideApplyButton = false,
  renderCatalog,
  className = '',
  datumScope = 'uf-simple',
  rootTabsDatumId,
  nestedToggleDatumId,
  nestedRemoveDatumId,
  nestedJoinerLabelDatumId,
  nestedJoinerControlDatumId,
  container = 'ui-tree',
  focusedGroupId,
  onFocusGroup,
  readOnly = false,
  narrow = false,
  capNarrowNesting = true,
  dnd = false,
  dragNodeId,
  onDragStartNode,
  onDragEndNode,
  canDropNode,
  onDropNode,
  keyboardCursorGroupId,
  onGripKeyDown,
  onGripBlur,
  onRequestAddCondition,
  problemOf,
  justAddedLeafId,
  onLeafAdded,
  defaultOpen = false,
  operatorFixed = false,
  onValueFocus,
}) => {
  const { t } = useTranslation();
  const [catalogOpen, setCatalogOpen] = React.useState(false);
  const [dragOver, setDragOver] = React.useState(false);
  // `group.id` is optional on the canonical `FilterGroup` type (a group read
  // from storage before ids were minted, or authored outside this tree, may
  // lack one — see types/filters.d.ts). Focus-tracking (`onFocusGroup` /
  // `focusedGroupId`) needs a stable identity regardless, so this instance
  // mints its own fallback once; it never gets persisted back onto `group`
  // because focus is local UI state, not part of the group's persisted shape.
  const mintedGroupId = React.useMemo(() => nextFilterId(), []);
  const groupId = group.id ?? mintedGroupId;
  const expressions = React.useMemo(() => group.expressions ?? [], [group.expressions]);
  const groups = React.useMemo(() => group.groups ?? [], [group.groups]);
  const childCount = expressions.length + groups.length;

  const leafProperties = React.useMemo(
    () =>
      excludePropertyTypes?.length
        ? availableProperties.filter((p) => !excludePropertyTypes.includes(p.type))
        : availableProperties,
    [availableProperties, excludePropertyTypes]
  );

  const setOperator = useCallback(
    (operator: 'and' | 'or') => {
      if (readOnly || operatorFixed) return;
      onChange({ ...group, operator });
    },
    [group, onChange, readOnly, operatorFixed]
  );

  const updateExpression = useCallback(
    (index: number, next: FilterGroupLeaf) =>
      onChange({ ...group, expressions: expressions.map((e, i) => (i === index ? next : e)) }),
    [group, expressions, onChange]
  );

  const removeExpression = useCallback(
    (index: number) => onChange({ ...group, expressions: expressions.filter((_, i) => i !== index) }),
    [group, expressions, onChange]
  );

  const updateGroup = useCallback(
    (index: number, next: FilterGroup) =>
      onChange({ ...group, groups: groups.map((g, i) => (i === index ? next : g)) }),
    [group, groups, onChange]
  );

  const removeGroup = useCallback(
    (index: number) => onChange({ ...group, groups: groups.filter((_, i) => i !== index) }),
    [group, groups, onChange]
  );

  const addCondition = useCallback(
    (expr: FilterGroupLeaf) => {
      onChange({ ...group, expressions: [...expressions, expr] });
      setCatalogOpen(false);
      const newLeafId = leafDragId(expr);
      if (newLeafId) onLeafAdded?.(newLeafId);
    },
    [group, expressions, onChange, onLeafAdded]
  );

  const addGroup = useCallback(() => {
    const child = newGroup();
    onChange({ ...group, groups: [...groups, child] });
    onFocusGroup?.(child.id);
  }, [group, groups, onChange, onFocusGroup]);

  // Narrow, nested (not root): the compact all/any chip header replaces the
  // "Group" eyebrow + item count (advanced-filters-v2.jsx `AFGroupNode`'s
  // `compact = inlineCatalog && !isRoot`).
  const compact = narrow && !isRoot;
  // Narrow hosts cap nesting at depth 1 — a group already at depth ≥ 1 offers
  // no further "Add group", only the "use Advanced" hint — unless the host is
  // the Advanced editor itself (`capNarrowNesting={false}`).
  const nestingCapped = narrow && capNarrowNesting && depth >= 1;
  // Drag-to-group is an edit: a read-only tree gets neither grips nor drop
  // targets, even on a host that opted into `dnd`.
  const dndActive = dnd && !readOnly;
  // Same for the operator: a read-only tree's joiners and compact chip render
  // as the plain-text echo, never as a control that silently does nothing.
  const onSetOp = readOnly || operatorFixed ? undefined : setOperator;

  const handleAddConditionClick = useCallback(() => {
    if (onRequestAddCondition) {
      onRequestAddCondition(groupId);
      return;
    }
    setCatalogOpen((v) => !v);
  }, [onRequestAddCondition, groupId]);

  const handleGripDragStart = useCallback(
    (e: React.DragEvent, nodeId: string | undefined) => {
      if (!dndActive || !nodeId) {
        e.preventDefault();
        return;
      }
      e.stopPropagation();
      setFilterDragPayload(e, { kind: 'group', nodeId });
      onDragStartNode?.(nodeId);
    },
    [dndActive, onDragStartNode]
  );

  const canDropHere = dndActive && !!dragNodeId && !!canDropNode && canDropNode(dragNodeId, groupId);
  const isKeyboardCursor = dndActive && keyboardCursorGroupId === groupId;

  // Split out of the component body so the group-drop-target wiring (a
  // three-callback object with its own early returns) doesn't add its own
  // nesting to the render function's cognitive complexity (SonarCloud
  // typescript:S3776, EAINT-931 R3-2).
  const buildDropHandlers = (): React.HTMLAttributes<HTMLDivElement> => {
    if (!dndActive) return {};
    return {
      onDragOver: (e: React.DragEvent) => {
        if (!canDropHere) return;
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'move';
        if (!dragOver) setDragOver(true);
      },
      onDragLeave: (e: React.DragEvent) => {
        if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
        setDragOver(false);
      },
      onDrop: (e: React.DragEvent) => {
        if (!canDropHere || !dragNodeId) return;
        e.preventDefault();
        e.stopPropagation();
        onDropNode?.(dragNodeId, groupId);
        setDragOver(false);
        onDragEndNode?.();
      },
    };
  };
  const dropHandlers = buildDropHandlers();

  const accentStyle = {
    '--fgn-accent': group.operator === 'and' ? 'var(--filter-group-and)' : 'var(--filter-group-or)',
  } as React.CSSProperties;

  const showHeader = !(isRoot && hideHeader);
  const focused = !isRoot && !!onFocusGroup && groupId === focusedGroupId;

  // A nested group becomes the "focused" (add-target) group when any control inside it
  // takes focus — a click on its operator toggle, a row, "Add condition", or reaching
  // one by keyboard. Focus bubbles in React, so the innermost group claims it and stops
  // the event before an enclosing group would take it back.
  const claimFocus = (e: React.FocusEvent) => {
    e.stopPropagation();
    onFocusGroup?.(groupId);
  };
  // Whether the joiner at `index` renders as the operator control (so its
  // datum id is the control's) rather than the read-only echo — mirrors
  // `FilterJoinerAtom`'s own rule: index 1, and only while editable.
  const joinerIsControl = (index: number) => index === 1 && !!onSetOp;

  // A child's drag grip (leaf or nested group alike) — only on an editable
  // `dnd` tree, and only for a node that has an id to drag by.
  const renderGrip = (dragId: string | undefined) =>
    dndActive &&
    dragId && (
      <button
        type="button"
        className="filter-group-node-molecule__grip"
        draggable
        onDragStart={(e) => handleGripDragStart(e, dragId)}
        onDragEnd={onDragEndNode}
        onKeyDown={(e) => onGripKeyDown?.(e, dragId, groupId)}
        onBlur={onGripBlur}
        aria-label={t('filters.tree.dragHandle')}
        title={t('filters.tree.dragHandle')}
        aria-pressed={dragNodeId === dragId}
      >
        <i className="pi pi-bars" aria-hidden="true" />
      </button>
    );

  /**
   * The header's operator control, in the wording each host reads it as: the
   * root's "Match all/any of the following", a narrow nested group's all/any
   * chip (which replaces the "Group" eyebrow and the item count —
   * `compactGroupHeaderIsAnAllAnyChip`), or a wide nested group's eyebrow +
   * toggle.
   */
  const renderHeaderOperator = () => {
    // A read-only tree echoes the operator as text ("all"/"any"), like its
    // joiners and compact chip, rather than an enabled toggle that can't change it.
    const operatorToggle = (datumId: string, datumKind?: 'tabs') => {
      if (readOnly) {
        return (
          <FilterJoinerAtom
            variant="quantifier"
            parentOperator={group.operator}
            datumId={datumId}
            datumKind={datumKind ?? 'toggle'}
          />
        );
      }
      const datumKindAttr = datumKind ? { 'data-kind': datumKind } : {};
      return (
        <SegmentedControlAtom
          mode="single"
          size="small"
          options={[
            { value: 'and', label: t('filters.filterGroups.matchAll') },
            { value: 'or', label: t('filters.filterGroups.matchAny') },
          ]}
          value={group.operator}
          onChange={(v) => setOperator(v as 'and' | 'or')}
          ariaLabel={t('filters.filterGroups.operatorLabel')}
          data-datum={datumId}
          {...datumKindAttr}
        />
      );
    };

    if (isRoot)
      return (
        <>
          <span className="filter-group-node-molecule__match-label">
            {t('filters.filterGroups.match')}
          </span>
          {operatorToggle(rootTabsDatumId ?? `${datumScope}.tabs`, 'tabs')}
          <span className="filter-group-node-molecule__of-label">
            {t('filters.filterGroups.ofFollowing')}
          </span>
        </>
      );

    if (compact)
      return (
        <>
          <FilterJoinerAtom
            variant="quantifier"
            parentOperator={group.operator}
            onSetOp={onSetOp}
            datumId={nestedToggleDatumId ?? `${datumScope}.toggle`}
            datumKind={nestedToggleDatumId ? 'button' : 'toggle'}
          />
          <span className="filter-group-node-molecule__compact-of-label">
            {t('filters.filterGroups.compactOfThese')}
          </span>
        </>
      );

    return (
      <>
        <span className="filter-group-node-molecule__group-label">
          {t('filters.tree.groupLabel')}
        </span>
        {operatorToggle(nestedToggleDatumId ?? `${datumScope}.toggle`)}
      </>
    );
  };

  // A single expression leaf's row (property or lane), split out of the
  // `expressions.map` body so its own layout/joiner branching doesn't add to
  // the render function's cognitive complexity (SonarCloud typescript:S3776,
  // EAINT-931 R3-2).
  const renderLeafChild = (leaf: FilterGroupLeaf, index: number) => {
    const dragId = leafDragId(leaf);
    const grip = renderGrip(dragId);
    // Opening is not focusing: `defaultOpen` may open every row, but only
    // the row just added takes focus — otherwise focus lands on whichever
    // open row happened to mount last.
    const justAdded = !!dragId && dragId === justAddedLeafId;
    const row = isPropertyLeaf(leaf) ? (
      <EditableFilterRowMolecule
        filter={leaf as FilterExpression}
        availableProperties={leafProperties}
        onChange={(next) => updateExpression(index, next)}
        onRemove={() => removeExpression(index)}
        scopeTypeLabels={scopeTypeLabels}
        arcTemplates={arcTemplates}
        availableRelationships={availableRelationships}
        familyTypeSchemas={familyTypeSchemas}
        availablePerspectives={availablePerspectives}
        perspectiveSearchProps={perspectiveSearchProps}
        className="filter-group-node-molecule__child-row"
        // `identity` (Sphere Type/Family) has no PropertyType of its own yet
        // (uf-identity ports it as `enum` today) — every property leaf marks
        // `aspect` until that lands, matching the prototype's default lane.
        lane="aspect"
        readOnly={readOnly}
        hideApplyButton={hideApplyButton}
        layout={narrow ? 'narrow' : 'row'}
        problem={leaf.id ? problemOf?.(leaf.id)?.message : undefined}
        defaultOpen={defaultOpen || justAdded}
        autoFocusValue={justAdded}
        onValueFocus={onValueFocus}
      />
    ) : (
      <LaneFilterRowMolecule
        leaf={leaf}
        onChange={(next) => updateExpression(index, next)}
        onRemove={() => removeExpression(index)}
        arcTemplates={arcTemplates}
        availableRelationships={availableRelationships}
        availablePerspectives={availablePerspectives}
        perspectiveSearchProps={perspectiveSearchProps}
        lockPerspective={lockPerspective}
        lockArc={lockArc}
        className="filter-group-node-molecule__child-row"
        readOnly={readOnly}
        layout={narrow ? 'narrow' : 'row'}
        problem={leaf.filter?.id ? problemOf?.(leaf.filter.id)?.message : undefined}
        // A wide lane row's editor floats; a host that opens every row
        // would stack one overlay per row, so there only the row just
        // added opens. Narrow rows expand in flow, like property rows.
        defaultOpen={justAdded || (narrow && defaultOpen)}
        autoFocusValue={justAdded}
      />
    );
    const leafJoinerDatumId = joinerIsControl(index)
      ? (nestedJoinerControlDatumId ?? `${datumScope}.label#2`)
      : (nestedJoinerLabelDatumId ?? `${datumScope}.label#2`);
    const joiner = (
      <FilterJoinerAtom
        index={index}
        parentOperator={group.operator}
        onSetOp={onSetOp}
        datumId={leafJoinerDatumId}
      />
    );

    return narrow ? (
      <div key={leafKey(leaf, index)} className="filter-group-node-molecule__child filter-group-node-molecule__child--narrow">
        {index > 0 && (
          <span className="filter-group-node-molecule__joiner-wrap filter-group-node-molecule__joiner-wrap--narrow">
            {joiner}
          </span>
        )}
        <span className="filter-group-node-molecule__row-narrow">
          {grip}
          <span className="filter-group-node-molecule__child-row-inner">{row}</span>
        </span>
      </div>
    ) : (
      <div key={leafKey(leaf, index)} className="filter-group-node-molecule__child">
        <span className="filter-group-node-molecule__gutter">
          {grip}
          <span className="filter-group-node-molecule__joiner-wrap">{joiner}</span>
        </span>
        {row}
      </div>
    );
  };

  // A single nested group's row, split out of the `groups.map` body for the
  // same reason as `renderLeafChild` above.
  const renderGroupChild = (child: FilterGroup & { id?: string }, index: number) => {
    const grip = renderGrip(child.id);
    const nested = (
      <FilterGroupNodeMolecule
        group={child}
        depth={depth + 1}
        allowGroups={allowGroups}
        availableProperties={availableProperties}
        excludePropertyTypes={excludePropertyTypes}
        scopeTypeLabels={scopeTypeLabels}
        arcTemplates={arcTemplates}
        availableRelationships={availableRelationships}
        familyTypeSchemas={familyTypeSchemas}
        availablePerspectives={availablePerspectives}
        perspectiveSearchProps={perspectiveSearchProps}
        lockPerspective={lockPerspective}
        lockArc={lockArc}
        hideApplyButton={hideApplyButton}
        renderCatalog={renderCatalog}
        datumScope={datumScope}
        nestedToggleDatumId={nestedToggleDatumId}
        nestedRemoveDatumId={nestedRemoveDatumId}
        nestedJoinerLabelDatumId={nestedJoinerLabelDatumId}
        nestedJoinerControlDatumId={nestedJoinerControlDatumId}
        container={container}
        focusedGroupId={focusedGroupId}
        onFocusGroup={onFocusGroup}
        onChange={(next) => updateGroup(index, next)}
        onRemove={() => removeGroup(index)}
        readOnly={readOnly}
        narrow={narrow}
        capNarrowNesting={capNarrowNesting}
        dnd={dnd}
        dragNodeId={dragNodeId}
        onDragStartNode={onDragStartNode}
        onDragEndNode={onDragEndNode}
        canDropNode={canDropNode}
        onDropNode={onDropNode}
        keyboardCursorGroupId={keyboardCursorGroupId}
        onGripKeyDown={onGripKeyDown}
        onGripBlur={onGripBlur}
        onRequestAddCondition={onRequestAddCondition}
        problemOf={problemOf}
        justAddedLeafId={justAddedLeafId}
        onLeafAdded={onLeafAdded}
        defaultOpen={defaultOpen}
        onValueFocus={onValueFocus}
      />
    );
    const groupJoinerIndex = expressions.length + index;
    const groupJoinerDatumId = joinerIsControl(groupJoinerIndex)
      ? (nestedJoinerControlDatumId ?? `${datumScope}.label`)
      : (nestedJoinerLabelDatumId ?? `${datumScope}.label`);
    const joiner = (
      <FilterJoinerAtom
        index={groupJoinerIndex}
        parentOperator={group.operator}
        onSetOp={onSetOp}
        datumId={groupJoinerDatumId}
      />
    );

    return narrow ? (
      <div key={child.id ?? index} className="filter-group-node-molecule__child filter-group-node-molecule__child--narrow">
        {groupJoinerIndex > 0 && (
          <span className="filter-group-node-molecule__joiner-wrap filter-group-node-molecule__joiner-wrap--narrow">
            {joiner}
          </span>
        )}
        <span className="filter-group-node-molecule__row-narrow">
          {grip}
          <div className="filter-group-node-molecule__nested">{nested}</div>
        </span>
      </div>
    ) : (
      <div key={child.id ?? index} className="filter-group-node-molecule__child">
        <span className="filter-group-node-molecule__gutter">
          {grip}
          <span className="filter-group-node-molecule__joiner-wrap">{joiner}</span>
        </span>
        <div className="filter-group-node-molecule__nested">{nested}</div>
      </div>
    );
  };

  const rootClassName = groupNodeClassName({
    isRoot,
    focused,
    compact,
    narrow,
    dragOver,
    canDropHere,
    isKeyboardCursor,
    className,
  });

  return (
    <div
      className={rootClassName}
      style={accentStyle}
      onFocus={!isRoot && onFocusGroup ? claimFocus : undefined}
      data-testid="filter-group-node"
      data-kind="group"
      data-depth={depth}
      data-container={container}
      {...dropHandlers}
    >
      {showHeader && (
        <div className="filter-group-node-molecule__header">
          {renderHeaderOperator()}
          {!compact && (
            <span className="filter-group-node-molecule__item-count">
              {t('filters.filterGroups.itemCount', { count: childCount })}
            </span>
          )}
          <span className="filter-group-node-molecule__spacer" />
          {!isRoot && !readOnly && (
            <ButtonAtom
              type="button"
              icon="pi pi-times"
              variant="ghost"
              shape="icon-square"
              size="small"
              className="filter-group-node-molecule__remove-group-btn"
              onClick={onRemove}
              aria-label={t('filters.filterGroups.removeGroup')}
              tooltip={t('filters.filterGroups.removeGroup')}
              data-datum={nestedRemoveDatumId ?? `${datumScope}.remove-group`}
            />
          )}
        </div>
      )}

      {/* A header-less root (`isRoot && hideHeader`) belongs to a framed host
          (`UnifiedFilterTreeMolecule`) that already renders its own empty-tree
          note above this node — showing this group-level note too would be a
          second, redundant "nothing here" message for the same empty root. */}
      {childCount === 0 && showHeader && (
        <p className="filter-group-node-molecule__empty">
          {canDropHere || isKeyboardCursor
            ? t('filters.filterGroups.emptyGroupCanDrop')
            : t('filters.filterGroups.emptyGroup')}
        </p>
      )}

      <div className="filter-group-node-molecule__children" data-container="each(node.children)">
        {expressions.map((leaf, index) => renderLeafChild(leaf, index))}
        {groups.map((child, index) => renderGroupChild(child, index))}
      </div>

      {!readOnly && (
        <div className="filter-group-node-molecule__add-row">
          <span className="filter-group-node-molecule__add-condition-anchor">
            <ButtonAtom
              type="button"
              label={narrow ? t('filters.tree.addConditionNarrow') : t('filters.tree.addCondition')}
              icon="pi pi-plus"
              variant="ghost"
              size="small"
              className="filter-group-node-molecule__add-condition-btn"
              onClick={handleAddConditionClick}
              // `.condition`, not `.add-condition` — the contract's id for this button
              // (advanced-filters-v2.jsx:1044's compact-catalog "Condition" pill, matched
              // by `uf-simple.condition`/`uf-advanced.condition`/`uf-column.condition#2`'s
              // twin). Without an explicit id it only matched by its own TEXT, which
              // collides post-normalisation with `FilterEditorPanelMolecule`'s "Condition"
              // eyebrow label — a capture with both on screen could match the eyebrow
              // (a `<div>`) for a datum the contract declares as a button (PAR-4/PAR-6,
              // EAINT-931).
              data-datum={`${datumScope}.condition`}
              data-kind="button"
            />
            {catalogOpen &&
              !onRequestAddCondition &&
              renderCatalog?.({ onPick: addCondition, onClose: () => setCatalogOpen(false) })}
          </span>
          {allowGroups && !nestingCapped && (
            <ButtonAtom
              type="button"
              label={t('filters.filterGroups.addGroup')}
              icon="pi pi-plus"
              variant="ghost"
              size="small"
              className="filter-group-node-molecule__add-group-btn"
              onClick={addGroup}
              data-datum={`${datumScope}.add-group`}
              data-testid="filter-group-node-add-group"
            />
          )}
          {allowGroups && nestingCapped && (
            <span className="filter-group-node-molecule__deeper-nesting-hint">
              {t('filters.filterGroups.deeperNestingHint')}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

FilterGroupNodeMolecule.displayName = 'FilterGroupNodeMolecule';

export default FilterGroupNodeMolecule;
export { newGroup as newFilterGroupNode };
